/**
 * InternalLinks — contextual cross-linking block
 * Boosts Google crawlability and PageRank flow between key pages.
 * Fully translated for EN, ES, RU, and ZH.
 */
import { Link, useLocation as useRouteLocation } from "react-router-dom";
import { ArrowRight, MapPin, Sparkles, BookOpen, HelpCircle } from "lucide-react";
import { useLocation, REGIONS } from "@/lib/locations";
import { SLUG_TO_ES } from "@/components/Navbar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const SERVICES = [
  { slug: "standard-clean", label: "Standard Cleaning", es: "Limpieza Estándar", ru: "Стандартная уборка", zh: "标准清洁" },
  { slug: "deep-clean", label: "Deep Cleaning", es: "Limpieza Profunda", ru: "Генеральная уборка", zh: "深度清洁" },
  { slug: "move-out-clean", label: "Move In / Out Cleaning", es: "Limpieza de Mudanza", ru: "Уборка при переезде", zh: "搬家清洁" },
  { slug: "post-construction-clean", label: "Post-Construction Cleaning", es: "Limpieza Post-Construcción", ru: "Уборка после ремонта", zh: "装修后清洁" },
  { slug: "commercial-clean", label: "Commercial Cleaning", es: "Limpieza Comercial", ru: "Коммерческая уборка", zh: "商业清洁" },
  { slug: "weekly-clean", label: "Weekly Cleaning", es: "Limpieza Semanal", ru: "Еженедельная уборка", zh: "每周清洁" },
  { slug: "biweekly-clean", label: "Bi-Weekly Cleaning", es: "Limpieza Quincenal", ru: "Уборка раз в две недели", zh: "每两周清洁" },
  { slug: "recurring-clean", label: "Recurring Cleaning", es: "Limpieza Recurrente", ru: "Регулярная уборка", zh: "定期清洁" },
  { slug: "one-time-clean", label: "One-Time Cleaning", es: "Limpieza de Una Vez", ru: "Разовая уборка", zh: "一次性清洁" },
  { slug: "same-day-clean", label: "Same-Day Cleaning", es: "Limpieza el Mismo Día", ru: "Уборка в тот же день", zh: "当日清洁" },
  { slug: "apartment-clean", label: "Apartment Cleaning", es: "Limpieza de Apartamentos", ru: "Уборка квартир", zh: "公寓清洁" },
  { slug: "move-in-clean", label: "Move-In Cleaning", es: "Limpieza de Entrada", ru: "Уборка перед въездом", zh: "入住清洁" },
  { slug: "airbnb-clean", label: "Airbnb Cleaning", es: "Limpieza de Airbnb", ru: "Уборка Airbnb", zh: "Airbnb 清洁" },
  { slug: "pet-friendly-clean", label: "Pet-Friendly Cleaning", es: "Limpieza con Mascotas", ru: "Уборка с питомцами", zh: "宠物友好清洁" },
  { slug: "eco-friendly-clean", label: "Eco-Friendly Cleaning", es: "Limpieza Ecológica", ru: "Эко-уборка", zh: "环保清洁" },
];

const LONG_TAIL_PAGES = [
  { slug: "busy-families", label: "Cleaning for Busy Families", es: "Limpieza para Familias Ocupadas", ru: "Уборка для занятых семей", zh: "忙碌家庭清洁" },
  { slug: "before-guests", label: "Cleaning Before Guests", es: "Limpieza Antes de Invitados", ru: "Уборка перед гостями", zh: "客前清洁" },
  { slug: "after-party", label: "After Party Cleaning", es: "Limpieza Después de Fiestas", ru: "Уборка после вечеринок", zh: "派对后清洁" },
  { slug: "last-minute", label: "Last-Minute Cleaning", es: "Limpieza de Última Hora", ru: "Срочная уборка", zh: "最后一分钟清洁" },
  { slug: "move-preparation", label: "Move Preparation Cleaning", es: "Limpieza para Preparar Mudanza", ru: "Уборка перед переездом", zh: "搬家准备清洁" },
  { slug: "seasonal", label: "Seasonal Cleaning", es: "Limpieza Estacional", ru: "Сезонная уборка", zh: "季节性清洁" },
  { slug: "homeowners-with-pets", label: "Cleaning for Homeowners with Pets", es: "Limpieza para Dueños de Mascotas", ru: "Уборка для владельцев питомцев", zh: "宠物主人清洁" },
];

export const InternalLinks = () => {
  const location = useLocation();
  const routeLoc = useRouteLocation();
  const currentRegion = REGIONS[location.regionSlug];
  
  const isEs = routeLoc.pathname.startsWith('/es');
  const isRu = routeLoc.pathname.startsWith('/ru');
  const isZh = routeLoc.pathname.startsWith('/zh');
  const langPrefix = isEs ? '/es' : isRu ? '/ru' : isZh ? '/zh' : '';

  const faqUrl = location.slug === "sacramento-area" ? `${langPrefix}/faq` : isEs ? `/es/preguntas-frecuentes` : isRu ? `/ru/faq` : isZh ? `/zh/faq` : `/locations/${location.slug}/faq`;
  const blogUrl = location.slug === "sacramento-area" ? `${langPrefix}/blog` : `${langPrefix}/locations/${location.slug}/blog`;

  const nearbyCities = currentRegion.cities
    .filter((c) => c.toLowerCase().replace(/\s+/g, "-") !== location.slug)
    .slice(0, 6);

  const t = {
    services: isEs ? "Nuestros Servicios" : isRu ? "Наши услуги" : isZh ? "我们的服务" : "Our Services",
    nearby: isEs ? "Áreas Cercanas" : isRu ? "Ближайшие районы" : isZh ? "附近地区" : "Nearby Areas",
    resources: isEs ? "Recursos" : isRu ? "Ресурсы" : isZh ? "资源" : "Resources",
    pricing: isEs ? "Precios y Paquetes" : isRu ? "Цены и пакеты" : isZh ? "价格与套餐" : "Pricing & Packages",
    allAreas: isEs ? "Todas las áreas de servicio →" : isRu ? "Все зоны обслуживания →" : isZh ? "所有服务区域 →" : "All Service Areas →",
    allServices: isEs ? "Todos los servicios" : isRu ? "Все услуги уборки" : isZh ? "所有清洁服务" : "All Cleaning Services",
    joinTeam: isEs ? "Únete a nuestro equipo" : isRu ? "Присоединяйтесь к нашей команде" : isZh ? "加入我们的团队" : "Join Our Team",
    sitemap: isEs ? "Mapa del sitio" : isRu ? "Карта сайта" : isZh ? "网站地图" : "Sitemap",
    faq: isEs ? `Preguntas Frecuentes en ${location.name}` : isRu ? `Частые вопросы в ${location.name}` : isZh ? `${location.name} 清洁常见问题` : `${location.name} Cleaning FAQ`,
    blog: isEs ? `Blog de Limpieza en ${location.name}` : isRu ? `Блог об уборке в ${location.name}` : isZh ? `${location.name} 清洁博客` : `${location.name} Cleaning Blog`,
    houseCleaningIn: (city: string) => isEs ? `Limpieza de casas en ${city}` : isRu ? `Уборка домов в ${city}` : isZh ? `${city} 房屋清洁` : `House Cleaning in ${city}`,
    inCity: (city: string) => isEs ? `en ${city}` : isRu ? `в ${city}` : isZh ? `在 ${city}` : `in ${city}`
  };

  const getCityUrl = (citySlug: string) => {
    if (!langPrefix) return `/locations/${citySlug}`;
    const mapped = SLUG_TO_ES[citySlug];
    if (mapped) {
      if (isEs) return mapped;
      if (isRu) return mapped.replace('/es/', '/ru/');
      if (isZh) return mapped.replace('/es/', '/zh/');
    }
    return `${langPrefix}/${citySlug}`;
  };

  const tTitle = isEs ? "Explorar Servicios y Áreas" : isRu ? "Обзор услуг и районов" : isZh ? "探索服务和区域" : "Explore Services & Areas";
  const tServicesHeader = isEs ? `Servicios en ${location.name}` : isRu ? `Услуги в ${location.name}` : isZh ? `${location.name} 服务` : `Services in ${location.name}`;
  const tCitiesHeader = isEs ? "Ciudades Cercanas" : isRu ? "Ближайшие города" : isZh ? "附近城市" : "Nearby Cities";

  return (
    <section className="py-12 bg-muted/20 border-t border-b">
      <div className="container">
        <div className="max-w-4xl mx-auto flex flex-col items-center text-center gap-6">
          <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            {tTitle}
          </h3>
          
          <Accordion type="single" collapsible className="w-full">
            {/* Services Accordion */}
            <AccordionItem value="services" className="border-b-0 mb-3 group">
              <AccordionTrigger className="bg-background px-6 py-4 rounded-xl border shadow-sm hover:no-underline hover:border-primary/50 hover:bg-muted/30 transition-all text-base font-semibold text-left">
                <span className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg group-hover:scale-110 transition-transform">
                    <Sparkles className="h-5 w-5 text-primary" />
                  </div>
                  {tServicesHeader}
                </span>
              </AccordionTrigger>
              <AccordionContent className="pt-4 pb-2 px-2">
                <div className="flex flex-wrap justify-center gap-2 md:gap-3">
                  {SERVICES.map((s) => (
                    <Link
                      key={s.slug}
                      to={langPrefix && s.slug === "move-out-clean" ? `${langPrefix}/limpieza-mudanza/${location.slug === 'sacramento-area' ? 'sacramento' : location.slug}` : langPrefix && s.slug === "deep-clean" ? `${langPrefix}/limpieza-profunda/${location.slug === 'sacramento-area' ? 'sacramento' : location.slug}` : `/services/${s.slug}`}
                      className="px-4 py-2 rounded-full bg-background border shadow-sm text-xs md:text-sm font-medium text-muted-foreground hover:text-primary hover:border-primary/50 hover:shadow-md transition-all"
                    >
                      {isEs ? s.es : isRu ? s.ru : isZh ? s.zh : s.label} {t.inCity(location.name)}
                    </Link>
                  ))}
                  {LONG_TAIL_PAGES.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/services/${s.slug}`}
                      className="px-4 py-2 rounded-full bg-background border shadow-sm text-xs md:text-sm font-medium text-muted-foreground hover:text-primary hover:border-primary/50 hover:shadow-md transition-all"
                    >
                      {isEs ? s.es : isRu ? s.ru : isZh ? s.zh : s.label} {t.inCity(location.name)}
                    </Link>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Nearby Cities Accordion */}
            <AccordionItem value="cities" className="border-b-0 mb-3 group">
              <AccordionTrigger className="bg-background px-6 py-4 rounded-xl border shadow-sm hover:no-underline hover:border-primary/50 hover:bg-muted/30 transition-all text-base font-semibold text-left">
                <span className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg group-hover:scale-110 transition-transform">
                    <MapPin className="h-5 w-5 text-primary" />
                  </div>
                  {tCitiesHeader}
                </span>
              </AccordionTrigger>
              <AccordionContent className="pt-4 pb-2 px-2">
                <div className="flex flex-wrap justify-center gap-2 md:gap-3">
                  {nearbyCities.map((city) => {
                    const slug = city.toLowerCase().replace(/\s+/g, "-");
                    return (
                      <Link
                        key={slug}
                        to={getCityUrl(slug)}
                        className="px-4 py-2 rounded-full bg-background border shadow-sm text-xs md:text-sm font-medium text-muted-foreground hover:text-primary hover:border-primary/50 hover:shadow-md transition-all"
                      >
                        <MapPin className="h-3.5 w-3.5 inline-block mr-1.5 -mt-0.5 opacity-70" />
                        {t.houseCleaningIn(city)}
                      </Link>
                    );
                  })}
                  <Link to={`${langPrefix}/locations`} className="px-4 py-2 rounded-full bg-background border shadow-sm text-xs md:text-sm font-medium text-muted-foreground hover:text-primary hover:border-primary/50 hover:shadow-md transition-all">
                    {t.allAreas}
                  </Link>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Resources Accordion */}
            <AccordionItem value="resources" className="border-b-0 group">
              <AccordionTrigger className="bg-background px-6 py-4 rounded-xl border shadow-sm hover:no-underline hover:border-primary/50 hover:bg-muted/30 transition-all text-base font-semibold text-left">
                <span className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg group-hover:scale-110 transition-transform">
                    <BookOpen className="h-5 w-5 text-primary" />
                  </div>
                  {t.resources}
                </span>
              </AccordionTrigger>
              <AccordionContent className="pt-4 pb-2 px-2">
                <div className="flex flex-wrap justify-center gap-2 md:gap-3">
                  <Link to={faqUrl} className="px-4 py-2 rounded-full bg-background border shadow-sm text-xs md:text-sm font-medium text-muted-foreground hover:text-primary hover:border-primary/50 hover:shadow-md transition-all">
                    <HelpCircle className="h-3.5 w-3.5 inline-block mr-1.5 -mt-0.5 opacity-70" />
                    {t.faq}
                  </Link>
                  <Link to={blogUrl} className="px-4 py-2 rounded-full bg-background border shadow-sm text-xs md:text-sm font-medium text-muted-foreground hover:text-primary hover:border-primary/50 hover:shadow-md transition-all">
                    <BookOpen className="h-3.5 w-3.5 inline-block mr-1.5 -mt-0.5 opacity-70" />
                    {t.blog}
                  </Link>
                  <Link to={`${langPrefix}/pricing`} className="px-4 py-2 rounded-full bg-background border shadow-sm text-xs md:text-sm font-medium text-muted-foreground hover:text-primary hover:border-primary/50 hover:shadow-md transition-all">
                    {t.pricing}
                  </Link>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    </section>
  );
};
